/**
 * PropertyForm — 2-Card-Layout nach Truth-Owner.
 *
 *   1. PUSHED TO CHANNELS  → Felder, die via Channex an Airbnb/Booking gehen
 *   2. INTERNAL            → Felder, die in StayOn bleiben (Cleaning, Invoice, Mailing)
 *
 * System-Info (Slug, Channex-ID, Provisioning, Timestamps) lebt im Audit-Tab,
 * Sidebar-Status-Pill und Meta-Strip — nicht hier im Edit-Form.
 *
 * Der Slug ist im Create-Mode oben in Card 1 editierbar. Nach Anlage ist er
 * immutable und nur noch im Audit-Tab sichtbar.
 */
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, type SubmitHandler } from 'react-hook-form';
import { Button } from '@/shared/components';
import type { PropertyResponse } from '@/shared/api';
import { ChannelsPushedSection } from './ChannelsPushedSection';
import { ChannexSection } from './ChannexSection';
import { InternalSection } from './InternalSection';
import { propertySchema, toFormDefaults, type PropertyValues } from './schema';

export interface PropertyFormProps {
  initial?: PropertyResponse;
  submitting?: boolean;
  /** Im Edit-Mode unterdruecken wir die Auto-Provision-Sektion (greift nur beim Anlegen). */
  showChannexProvisioning?: boolean;
  onSubmit: (values: PropertyValues) => void | Promise<void>;
  onCancel: () => void;
  submitLabel?: string;
  addressEditable?: boolean;
}

export function PropertyForm({
  initial,
  submitting,
  showChannexProvisioning = !initial,
  onSubmit,
  onCancel,
  submitLabel = 'Speichern',
  addressEditable = !initial,
}: PropertyFormProps) {
  const {
    register,
    control,
    setValue,
    watch,
    handleSubmit,
    formState: { errors, isDirty },
  } = useForm<PropertyValues>({
    resolver: zodResolver(propertySchema),
    defaultValues: toFormDefaults(initial),
  });

  const submit: SubmitHandler<PropertyValues> = (values) => onSubmit(values);
  const isCreate = !initial;

  return (
    <form onSubmit={handleSubmit(submit)} className="flex flex-col gap-6">
      <ChannelsPushedSection
        register={register}
        control={control}
        errors={errors}
        setValue={setValue}
        watch={watch}
        slugEditable={isCreate}
        addressEditable={addressEditable}
      />
      <InternalSection register={register} control={control} errors={errors} />
      {showChannexProvisioning && <ChannexSection register={register} />}

      <div className="flex justify-end gap-2">
        <Button type="button" variant="ghost" onClick={onCancel} disabled={submitting || !isDirty}>
          Verwerfen
        </Button>
        <Button type="submit" variant="brand" loading={submitting} disabled={!isDirty}>
          {submitLabel}
        </Button>
      </div>
    </form>
  );
}
